import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Box, Stack, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';

import { breakoutAPI } from '../services/api';
import {
  BreakoutCandidatesTable,
  BreakoutDetailDrawer,
  BreakoutFilterSkeleton,
  BreakoutFiltersBar,
  BreakoutPageHeader,
  BreakoutSummaryHeader,
  C,
  getLocale,
  getLocalizedText,
  prettySector,
} from '../components/breakout';

// Thin orchestrator: owns state, fetches, derived stats, and composes the
// feature components imported from `components/breakout/`.
// Renderer-only: no UI-side investor logic, no thresholds invented in the page.
const BreakoutCandidates = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'));
  const locale = useMemo(() => getLocale(i18n.language), [i18n.language]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [metadataError, setMetadataError] = useState('');
  const [detailDrawer, setDetailDrawer] = useState(null);
  const [detailHistory, setDetailHistory] = useState(null);
  const [detailHistoryLoading, setDetailHistoryLoading] = useState(false);
  const [exportingImage, setExportingImage] = useState(false);
  const [drawerExporting, setDrawerExporting] = useState(false);

  const tableExportRef = useRef(null);
  const detailDrawerExportRef = useRef(null);
  const detailDrawerBodyRef = useRef(null);

  const [filters, setFilters] = useState({
    preset: 'balanced',
    min_score: 65,
    signal: '',
    market_regime: '',
    strong_only: false,
    exclude_trigger_none: true,
    limit: 50,
  });

  // ---- Metadata lookup ------------------------------------------------------

  const metadataMaps = useMemo(() => ({
    presets: new Map((metadata?.presets || []).map((item) => [item.code, item])),
    signals: new Map((metadata?.signals || []).map((item) => [item?.label?.code, item])),
    marketRegimes: new Map((metadata?.market_regimes || []).map((item) => [item.code, item])),
  }), [metadata]);

  const getMetadataLabel = useCallback((group, code, variant = 'label') => {
    if (!code) return '—';
    if (group === 'signals') {
      const entry = metadataMaps.signals.get(String(code).toUpperCase());
      return getLocalizedText(entry?.[variant], i18n.language);
    }
    if (group === 'presets') {
      return getLocalizedText(metadataMaps.presets.get(String(code).toUpperCase()), i18n.language);
    }
    if (group === 'marketRegimes') {
      return getLocalizedText(metadataMaps.marketRegimes.get(String(code).toUpperCase()), i18n.language);
    }
    return '—';
  }, [i18n.language, metadataMaps]);

  // ---- API loaders ----------------------------------------------------------

  const loadMetadata = useCallback(async () => {
    try {
      setMetadataError('');
      const res = await breakoutAPI.getMetadata();
      setMetadata(res.data);
    } catch (e) {
      setMetadata(null);
      setMetadataError(
        e?.response?.data?.detail || e.message || t('breakoutCandidates.loadingError'),
      );
    }
  }, [t]);

  const load = useCallback(async ({ forceRefresh = false } = {}) => {
    setLoading(true);
    setError('');
    try {
      const params = {
        preset: filters.preset,
        min_score: filters.min_score,
        limit: filters.limit,
        strong_only: filters.strong_only,
        exclude_trigger_none: filters.exclude_trigger_none,
        refresh_if_stale: true,
      };
      if (forceRefresh) params.force_refresh = true;
      if (filters.signal) params.signal = filters.signal;
      if (filters.market_regime) params.market_regime = filters.market_regime;
      const res = await breakoutAPI.getCandidates(params);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || t('breakoutCandidates.loadingError'));
    } finally {
      setLoading(false);
    }
  }, [filters, t]);

  useEffect(() => { loadMetadata(); }, [loadMetadata]);
  useEffect(() => { load(); }, [load]);

  // ---- Derived state --------------------------------------------------------

  const candidates = useMemo(() => data?.candidates || [], [data?.candidates]);

  const stats = useMemo(() => {
    const buy = candidates.filter((x) => x.signal === 'BUY').length;
    const watch = candidates.filter((x) => x.signal === 'BUY_WATCH').length;
    const avg = candidates.length
      ? candidates.reduce((s, x) => s + (x.breakout_score || 0), 0) / candidates.length
      : 0;
    const max = candidates.reduce((m, x) => Math.max(m, x.breakout_score || 0), 0);
    const sectorCount = candidates.reduce((acc, c) => {
      const k = prettySector(c.sector, i18n.language);
      if (k && k !== '—') acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    const topSector = Object.entries(sectorCount).sort((a, b) => b[1] - a[1])[0];
    const regimeCount = candidates.reduce((acc, c) => {
      const k = c.market_regime || 'UNKNOWN';
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    return { buy, watch, avg, max, topSector, regimeCount };
  }, [candidates, i18n.language]);

  const regimeWarning = useMemo(() => {
    if (filters.preset !== 'preparation_phase' || candidates.length === 0) return null;
    const dominant = Object.entries(stats.regimeCount).sort((a, b) => b[1] - a[1])[0]?.[0];
    if (dominant === 'RECOVERY') return t('breakoutCandidates.warnings.recovery');
    if (dominant === 'RISK_OFF') return t('breakoutCandidates.warnings.riskOff');
    if (dominant === 'RISK_ON' && candidates.length > 30) {
      return t('breakoutCandidates.warnings.riskOn');
    }
    return null;
  }, [filters.preset, candidates, stats, t]);

  const dominantRegimeCode = useMemo(
    () => Object.entries(stats.regimeCount).sort((a, b) => b[1] - a[1])[0]?.[0] || null,
    [stats.regimeCount],
  );

  // ---- Handlers -------------------------------------------------------------

  const openDetail = useCallback((row) => setDetailDrawer(row), []);
  const closeDetail = useCallback(() => setDetailDrawer(null), []);
  const handleApply = useCallback(() => load(), [load]);
  const handleRefresh = useCallback(() => load({ forceRefresh: true }), [load]);

  const handleExportTableImage = useCallback(async () => {
    if (!tableExportRef.current || exportingImage || candidates.length === 0) return;
    try {
      setExportingImage(true);
      const canvas = await html2canvas(tableExportRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
      });
      const link = document.createElement('a');
      const asOfDate = data?.score_date || new Date().toISOString().slice(0, 10);
      link.download = `breakout-candidates-${filters.preset}-${asOfDate}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } finally {
      setExportingImage(false);
    }
  }, [candidates.length, data?.score_date, exportingImage, filters.preset]);

  const handleExportDrawerImage = useCallback(async () => {
    const wrapper = detailDrawerExportRef.current;
    const body = detailDrawerBodyRef.current;
    if (!wrapper || !detailDrawer || drawerExporting) return;

    const savedWrapperHeight = wrapper.style.height;
    const savedBodyOverflow = body ? body.style.overflowY : '';
    const savedBodyMaxHeight = body ? body.style.maxHeight : '';
    const savedBodyHeight = body ? body.style.height : '';

    setDrawerExporting(true);
    try {
      wrapper.style.height = 'auto';
      if (body) {
        body.style.overflowY = 'visible';
        body.style.maxHeight = 'none';
        body.style.height = 'auto';
      }
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const canvas = await html2canvas(wrapper, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
        windowHeight: wrapper.scrollHeight,
      });
      const link = document.createElement('a');
      const asOfDate = detailDrawer?.score_date || new Date().toISOString().slice(0, 10);
      link.download = `${detailDrawer?.ticker || 'breakout'}-candidate-${asOfDate}.png`;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      console.error('Failed to export breakout candidate drawer image:', e);
    } finally {
      wrapper.style.height = savedWrapperHeight;
      if (body) {
        body.style.overflowY = savedBodyOverflow;
        body.style.maxHeight = savedBodyMaxHeight;
        body.style.height = savedBodyHeight;
      }
      setDrawerExporting(false);
    }
  }, [detailDrawer, drawerExporting]);

  // ---- Per-ticker signal history --------------------------------------------

  useEffect(() => {
    if (!detailDrawer?.ticker) {
      setDetailHistory(null);
      setDetailHistoryLoading(false);
      return undefined;
    }
    let active = true;
    const loadTickerHistory = async () => {
      setDetailHistoryLoading(true);
      setDetailHistory(null);
      try {
        const res = await breakoutAPI.getTickerHistory(detailDrawer.ticker, {
          preset: filters.preset,
          days: 60,
        });
        if (active) setDetailHistory(res.data);
      } catch (e) {
        if (active) {
          setDetailHistory({
            history: [],
            error:
              e?.response?.data?.detail
              || e.message
              || t('breakoutCandidates.drawer.signalHistoryError'),
          });
        }
      } finally {
        if (active) setDetailHistoryLoading(false);
      }
    };
    loadTickerHistory();
    return () => { active = false; };
  }, [detailDrawer?.ticker, filters.preset, t]);

  // ---- Render ---------------------------------------------------------------

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, maxWidth: 1700, mx: 'auto' }}>
      <Stack spacing={1.8}>
        <BreakoutPageHeader
          t={t}
          scoreDate={data?.score_date}
          candidatesCount={candidates.length}
          exportingImage={exportingImage}
          loading={loading}
          onExport={handleExportTableImage}
          onRefresh={handleRefresh}
        />

        {!metadata && !metadataError ? (
          <BreakoutFilterSkeleton />
        ) : (
          <BreakoutFiltersBar
            t={t}
            language={i18n.language}
            metadata={metadata}
            filters={filters}
            setFilters={setFilters}
            getMetadataLabel={getMetadataLabel}
            onApply={handleApply}
            loading={loading}
          />
        )}

        {error ? <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert> : null}
        {metadataError ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>{metadataError}</Alert>
        ) : null}
        {regimeWarning ? (
          <Alert severity="warning" sx={{ borderRadius: 2, fontSize: 12.5 }}>{regimeWarning}</Alert>
        ) : null}

        <Box ref={tableExportRef}>
          <BreakoutSummaryHeader
            loading={loading}
            candidates={candidates}
            isCompact={isCompact}
            stats={stats}
            dominantRegimeCode={dominantRegimeCode}
            t={t}
            getMetadataLabel={getMetadataLabel}
          />
          <BreakoutCandidatesTable
            loading={loading}
            candidates={candidates}
            isCompact={isCompact}
            t={t}
            language={i18n.language}
            locale={locale}
            onOpenDetail={openDetail}
          />
        </Box>

        <Typography sx={{ fontSize: 11, color: C.mutedSoft, textAlign: 'right' }}>
          {t('breakoutCandidates.footerNote')}
        </Typography>

        <BreakoutDetailDrawer
          detailDrawer={detailDrawer}
          onClose={closeDetail}
          isCompact={isCompact}
          drawerExporting={drawerExporting}
          onExport={handleExportDrawerImage}
          drawerRef={detailDrawerExportRef}
          bodyRef={detailDrawerBodyRef}
          t={t}
          language={i18n.language}
          locale={locale}
          detailHistory={detailHistory}
          detailHistoryLoading={detailHistoryLoading}
          getMetadataLabel={getMetadataLabel}
        />
      </Stack>
    </Box>
  );
};

export default BreakoutCandidates;
